import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import LanguageSelector, { useLanguage } from '../../components/feature/LanguageSelector';
import { translations } from '../../mocks/translations';
import { useSEO, generateServiceSchema } from '../../utils/seo';

const AIRPORT_PRICES: Record<string, { pricePerPerson: number; minPeople: number }> = {
  'puerto-plata': { pricePerPerson: 50, minPeople: 1 },
  'santiago': { pricePerPerson: 80, minPeople: 1 },
  'santo-domingo': { pricePerPerson: 100, minPeople: 2 },
  'punta-cana': { pricePerPerson: 200, minPeople: 2 },
};

export default function TransfersPage() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState('van');
  const language = useLanguage();
  const t = translations[language];

  // SEO Configuration
  useSEO({
    title: 'Traslados Aeropuerto Puerto Plata - POP, PUJ, SDQ, STI | Leury Tours',
    description: 'Traslados profesionales desde aeropuertos de República Dominicana: Puerto Plata (POP), Punta Cana (PUJ), Santo Domingo (SDQ), Santiago (STI). Servicio seguro, puntual y económico desde $50.',
    keywords: 'traslado aeropuerto Puerto Plata, transfer POP, traslado PUJ, transporte aeropuerto República Dominicana, taxi aeropuerto Puerto Plata',
    ogImage: 'https://static.readdy.ai/image/b7cf4d15025d84086817049d392fc8bc/adacd0c79cdca09a503af18efa205f12.jpeg',
    schema: generateServiceSchema({
      name: 'Traslados al Aeropuerto',
      description: 'Servicio profesional de traslados desde y hacia aeropuertos en República Dominicana',
      price: 50
    })
  });
  
  const [formData, setFormData] = useState({
    airport: '',
    destination: '',
    tripType: 'one-way' as 'one-way' | 'round-trip',
    date: '',
    time: '',
    passengers: '2',
    vehicle: 'van'
  });
  const [price, setPrice] = useState(0);
  const [pricePerPerson, setPricePerPerson] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const vehicles = [
    {
      id: 'van',
      name: 'Van',
      capacity: '2-11',
      price: 50,
      image: 'https://static.readdy.ai/image/b7cf4d15025d84086817049d392fc8bc/adacd0c79cdca09a503af18efa205f12.jpeg',
      features: language === 'en' 
        ? ['Air Conditioning', 'Spacious Interior', 'Luggage Space', 'Professional Driver']
        : ['Aire Acondicionado', 'Interior Espacioso', 'Espacio para Equipaje', 'Conductor Profesional']
    }
  ];

  const airportOptions = [
    { id: 'puerto-plata', label: t.transfers.puertoPlataAirport, price: 50, min: 1 },
    { id: 'santiago', label: t.transfers.santiagoAirport, price: 80, min: 1 },
    { id: 'punta-cana', label: t.transfers.puntaCanaAirport, price: 200, min: 2 },
    { id: 'santo-domingo', label: t.transfers.santoDomingoAirport, price: 100, min: 2 },
  ];

  const airports = [
    {
      code: 'POP',
      name: 'Puerto Plata',
      fullName: 'Gregorio Luperón International Airport',
      time: '15-30 min'
    },
    {
      code: 'STI',
      name: 'Santiago',
      fullName: 'Cibao International Airport',
      time: '1-1.5 hrs'
    },
    {
      code: 'PUJ',
      name: 'Punta Cana',
      fullName: 'Punta Cana International Airport',
      time: '4-5 hrs'
    },
    {
      code: 'SDQ',
      name: 'Santo Domingo',
      fullName: 'Las Américas International Airport',
      time: '3-4 hrs'
    }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    if (!formData.airport) {
      setPrice(0);
      setPricePerPerson(0);
      return;
    }

    const airportData = AIRPORT_PRICES[formData.airport];
    if (!airportData) {
      setPrice(0);
      setPricePerPerson(0);
      return;
    }

    const numPassengers = Math.max(parseInt(formData.passengers) || 1, airportData.minPeople);
    const basePricePerPerson = airportData.pricePerPerson;
    const tripMultiplier = formData.tripType === 'round-trip' ? 2 : 1;
    const totalPrice = basePricePerPerson * numPassengers * tripMultiplier;

    setPricePerPerson(basePricePerPerson * tripMultiplier);
    setPrice(totalPrice);
  }, [formData.airport, formData.passengers, formData.tripType]);

  const getMinPassengers = () => {
    if (!formData.airport) return 1;
    const airportData = AIRPORT_PRICES[formData.airport];
    return airportData ? airportData.minPeople : 1;
  };

  const handleAirportChange = (airportId: string) => {
    const airportData = AIRPORT_PRICES[airportId];
    const currentPassengers = parseInt(formData.passengers) || 1;
    const minPeople = airportData ? airportData.minPeople : 1;
    const newPassengers = currentPassengers < minPeople ? String(minPeople) : formData.passengers;
    
    setFormData({ ...formData, airport: airportId, passengers: newPassengers });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.airport || !formData.destination || !formData.date || !formData.time || !formData.passengers) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams({
        airport: formData.airport,
        destination: formData.destination,
        tripType: formData.tripType,
        date: formData.date,
        time: formData.time,
        passengers: formData.passengers,
        vehicle: formData.vehicle,
        total_price: `$${price}`
      }).toString();

      const response = await fetch('https://readdy.ai/api/public/form/submit/b7cf4d15025d84086817049d392fc8bc/transfer-booking', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          airport: '',
          destination: '',
          tripType: 'one-way',
          date: '',
          time: '',
          passengers: '2',
          vehicle: 'van'
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentVehicle = vehicles.find(v => v.id === selectedVehicle);
  const selectedAirportData = formData.airport ? AIRPORT_PRICES[formData.airport] : null;
  const hasMinimum = selectedAirportData && selectedAirportData.minPeople > 1;

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 sm:gap-3">
              <img src="https://static.readdy.ai/image/b7cf4d15025d84086817049d392fc8bc/8ae7b2cd5c5fe056440d4fe4564ccd66.jpeg" alt="Leury Tours" className="h-10 sm:h-12 w-auto" />
              <span className={`text-lg sm:text-xl font-bold ${scrolled ? 'text-[#0A2463]' : 'text-white'}`}>Leury Tours</span>
            </Link>
            
            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-6 xl:gap-8">
              <Link to="/" className={`text-sm font-medium transition-colors ${scrolled ? 'text-[#0A2463] hover:text-[#00B4D8]' : 'text-white hover:text-[#48CAE4]'}`}>{t.nav.home}</Link>
              <Link to="/about" className={`text-sm font-medium transition-colors ${scrolled ? 'text-[#0A2463] hover:text-[#00B4D8]' : 'text-white hover:text-[#48CAE4]'}`}>{t.nav.about}</Link>
              <Link to="/tours" className={`text-sm font-medium transition-colors ${scrolled ? 'text-[#0A2463] hover:text-[#00B4D8]' : 'text-white hover:text-[#48CAE4]'}`}>{t.nav.tours}</Link>
              <Link to="/transfers" className={`text-sm font-medium transition-colors ${scrolled ? 'text-[#00B4D8]' : 'text-[#48CAE4]'}`}>{t.nav.transfers}</Link>
              <a href="/#contact" className={`text-sm font-medium transition-colors ${scrolled ? 'text-[#0A2463] hover:text-[#00B4D8]' : 'text-white hover:text-[#48CAE4]'}`}>{t.nav.contact}</a>
              <LanguageSelector scrolled={scrolled} />
              <a href="/#booking" className="bg-[#FF6B6B] text-white px-5 xl:px-6 py-2.5 rounded-full text-sm font-semibold hover:bg-[#ff5252] transition-all whitespace-nowrap">{t.nav.bookNow}</a>
            </div>

            {/* Mobile Menu Button */}
            <div className="flex items-center gap-3 lg:hidden">
              <LanguageSelector scrolled={scrolled} />
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className={`p-2 rounded-lg ${scrolled ? 'text-[#0A2463]' : 'text-white'}`}
              >
                <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 top-[60px] bg-white z-40 overflow-y-auto">
            <div className="px-4 py-6 space-y-4">
              <Link to="/" onClick={() => setMobileMenuOpen(false)} className="block text-base font-medium text-[#0A2463] py-3 border-b border-gray-100">{t.nav.home}</Link>
              <Link to="/about" onClick={() => setMobileMenuOpen(false)} className="block text-base font-medium text-[#0A2463] py-3 border-b border-gray-100">{t.nav.about}</Link>
              <Link to="/tours" onClick={() => setMobileMenuOpen(false)} className="block text-base font-medium text-[#0A2463] py-3 border-b border-gray-100">{t.nav.tours}</Link>
              <Link to="/transfers" onClick={() => setMobileMenuOpen(false)} className="block text-base font-medium text-[#00B4D8] py-3 border-b border-gray-100">{t.nav.transfers}</Link>
              <a href="/#contact" onClick={() => setMobileMenuOpen(false)} className="block text-base font-medium text-[#0A2463] py-3 border-b border-gray-100">{t.nav.contact}</a>
              <a href="/#booking" onClick={() => setMobileMenuOpen(false)} className="block w-full bg-[#FF6B6B] text-white px-6 py-3 rounded-full text-base font-semibold text-center mt-6">{t.nav.bookNow}</a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative h-[50vh] sm:h-[60vh] lg:h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://static.readdy.ai/image/b7cf4d15025d84086817049d392fc8bc/adacd0c79cdca09a503af18efa205f12.jpeg" alt="Airport Transfer" className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#0A2463]/80 via-[#0A2463]/50 to-transparent"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 w-full text-center pt-16">
          <div className="flex items-center justify-center gap-2 mb-4 sm:mb-6">
            <i className="ri-car-line text-[#48CAE4] text-lg sm:text-xl"></i>
            <span className="text-white/90 text-xs sm:text-sm font-medium tracking-wide">{t.transfers.heroSubtitle}</span>
          </div>
          <h1 className="text-3xl sm:text-5xl lg:text-6xl xl:text-7xl font-black text-white leading-tight mb-4 sm:mb-6">
            {t.transfers.heroTitle}
          </h1>
          <p className="text-sm sm:text-base lg:text-xl text-white/90 mb-6 sm:mb-8 font-light max-w-2xl mx-auto">{t.transfers.heroDescription}</p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <a href="#calculator" className="bg-[#00B4D8] text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full text-sm sm:text-base font-semibold hover:bg-[#0096b8] transition-all flex items-center justify-center gap-2 whitespace-nowrap">
              {t.transfers.bookNow} <i className="ri-arrow-right-line"></i>
            </a>
            <a href="#airports" className="bg-transparent border-2 border-white text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full text-sm sm:text-base font-semibold hover:bg-white hover:text-[#0A2463] transition-all whitespace-nowrap text-center">
              {t.transfers.allAirports}
            </a>
          </div>
        </div>
      </section>

      {/* Airports Section */}
      <section id="airports" className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12">
            <span className="text-[#00B4D8] text-xs sm:text-sm font-semibold tracking-wider uppercase">{t.transfers.allAirports}</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0A2463] mt-2 mb-3 sm:mb-4">{t.transfers.airportsTitle}</h2>
            <p className="text-gray-600 text-sm sm:text-base lg:text-lg max-w-2xl mx-auto">{t.transfers.airportsDescription}</p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-8 sm:mb-12">
            {airports.map((airport) => {
              const airportKey = airport.name.toLowerCase().replace(' ', '-') as string;
              const priceData = AIRPORT_PRICES[airportKey];
              return (
                <div key={airport.code} className="bg-gradient-to-br from-[#FFF9F0] to-white rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-gray-100 hover:shadow-lg transition-all group">
                  <div className="w-10 h-10 sm:w-14 sm:h-14 bg-[#00B4D8]/10 rounded-lg sm:rounded-xl flex items-center justify-center mb-3 sm:mb-4 group-hover:bg-[#00B4D8]/20 transition-all">
                    <i className="ri-plane-line text-[#00B4D8] text-lg sm:text-2xl"></i>
                  </div>
                  <h3 className="text-sm sm:text-lg font-bold text-[#0A2463] mb-1 sm:mb-2">{airport.name} ({airport.code})</h3>
                  <p className="text-gray-600 text-[10px] sm:text-sm mb-2 sm:mb-3 line-clamp-2">{airport.fullName}</p>
                  <div className="flex items-center gap-1.5 sm:gap-2 text-[#00B4D8] mb-2">
                    <i className="ri-time-line text-xs sm:text-sm"></i>
                    <span className="text-[10px] sm:text-sm font-medium">{airport.time} {t.transfers.toPuertoPlata}</span>
                  </div>
                  {priceData && (
                    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-gray-100">
                      <div className="flex items-center gap-1 sm:gap-2">
                        <i className="ri-price-tag-3-line text-[#FF6B6B] text-xs sm:text-sm"></i>
                        <span className="text-xs sm:text-sm font-bold text-[#0A2463]">${priceData.pricePerPerson} <span className="font-normal text-gray-500">/ {t.transfers.perPerson}</span></span>
                      </div>
                      {priceData.minPeople > 1 && (
                        <span className="text-[10px] sm:text-xs text-[#FF6B6B] font-medium">({t.transfers.minimumPeople})</span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Map Section */}
          <div className="bg-white rounded-2xl sm:rounded-3xl overflow-hidden shadow-xl border border-gray-100">
            <div className="p-4 sm:p-6 bg-gradient-to-r from-[#0A2463] to-[#0A2463]/90">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white/10 rounded-lg flex items-center justify-center">
                  <i className="ri-map-pin-line text-[#48CAE4] text-lg sm:text-xl"></i>
                </div>
                <div>
                  <h3 className="text-white font-bold text-sm sm:text-lg">{t.transfers.destinations}</h3>
                  <p className="text-white/70 text-xs sm:text-sm">{t.transfers.destinationsDescription}</p>
                </div>
              </div>
            </div>
            <div className="relative w-full h-64 sm:h-80 lg:h-[450px]">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1930476.7006893635!2d-71.24831095!3d18.89549645!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8eaf8838ef497da5%3A0xc30de964e7e3088e!2sDominican%20Republic!5e0!3m2!1sen!2sus!4v1704067200000!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Dominican Republic Map"
                className="absolute inset-0"
              ></iframe>
            </div>
            <div className="p-3 sm:p-4 bg-gray-50 border-t border-gray-100">
              <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-6 text-[10px] sm:text-sm">
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <div className="w-2 h-2 sm:w-3 sm:h-3 bg-[#00B4D8] rounded-full"></div>
                  <span className="text-gray-600">Puerto Plata — $50</span>
                </div>
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <div className="w-2 h-2 sm:w-3 sm:h-3 bg-[#FF6B6B] rounded-full"></div>
                  <span className="text-gray-600">Santiago — $80</span>
                </div>
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <div className="w-2 h-2 sm:w-3 sm:h-3 bg-[#48CAE4] rounded-full"></div>
                  <span className="text-gray-600">Punta Cana — $200</span>
                </div>
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <div className="w-2 h-2 sm:w-3 sm:h-3 bg-[#0A2463] rounded-full"></div>
                  <span className="text-gray-600">Santo Domingo — $100</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Fleet Section */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0A2463] mb-3 sm:mb-4">{t.transfers.ourFleet}</h2>
            <p className="text-gray-600 text-sm sm:text-base lg:text-lg">{t.transfers.chooseVehicle}</p>
          </div>

          {currentVehicle && (
            <div className="max-w-5xl mx-auto">
              <div className="bg-white rounded-2xl sm:rounded-3xl overflow-hidden shadow-xl">
                <div className="grid md:grid-cols-2">
                  <div className="relative h-64 sm:h-80 lg:h-96">
                    <img src={currentVehicle.image} alt={currentVehicle.name} className="w-full h-full object-cover object-center" />
                    <div className="absolute top-4 sm:top-6 left-4 sm:left-6">
                      <div className="bg-[#00B4D8] text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-full font-bold text-xs sm:text-sm">
                        {t.transfers.from} ${currentVehicle.price}
                      </div>
                    </div>
                  </div>
                  <div className="p-5 sm:p-6 lg:p-8 xl:p-12 flex flex-col justify-center">
                    <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0A2463] mb-3 sm:mb-4">{currentVehicle.name}</h3>
                    <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
                      <div className="flex items-center gap-1.5 sm:gap-2">
                        <i className="ri-user-line text-[#00B4D8] text-lg sm:text-xl"></i>
                        <span className="text-gray-600 text-xs sm:text-sm">{t.transfers.capacity}: {currentVehicle.capacity} {t.transfers.passengers}</span>
                      </div>
                    </div>
                    <div className="space-y-2 sm:space-y-3 mb-6 sm:mb-8">
                      {currentVehicle.features.map((feature, index) => (
                        <div key={index} className="flex items-center gap-2 sm:gap-3">
                          <i className="ri-checkbox-circle-line text-[#00B4D8] text-base sm:text-lg"></i>
                          <span className="text-gray-700 text-xs sm:text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>
                    <a href="#calculator" className="inline-flex items-center justify-center gap-2 bg-[#00B4D8] text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold hover:bg-[#0096b8] transition-all text-xs sm:text-sm whitespace-nowrap">
                      {t.transfers.bookVehicle} <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Calculator Section */}
      <section id="calculator" className="py-12 sm:py-16 lg:py-24 bg-gradient-to-b from-[#FFF9F0] to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0A2463] mb-3 sm:mb-4">{t.transfers.calculateTransfer}</h2>
            <p className="text-gray-600 text-sm sm:text-base lg:text-lg">{t.transfers.calculateDescription}</p>
          </div>

          <div className="max-w-3xl mx-auto bg-white rounded-2xl sm:rounded-3xl shadow-xl p-5 sm:p-6 lg:p-8 xl:p-12">
            <form id="transfer-form" data-readdy-form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              
              {/* Airport Selection */}
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-[#0A2463] mb-2">{t.transfers.airport}</label>
                <select
                  name="airport"
                  value={formData.airport}
                  onChange={(e) => handleAirportChange(e.target.value)}
                  className="w-full px-3 sm:px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00B4D8] text-xs sm:text-sm cursor-pointer"
                  required
                >
                  <option value="">{t.transfers.selectAirport}</option>
                  {airportOptions.map(airport => (
                    <option key={airport.id} value={airport.id}>
                      {airport.label} — ${airport.price}/{t.transfers.perPerson} {airport.min > 1 ? `(${t.transfers.minimumPeople})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Trip Type Toggle */}
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-[#0A2463] mb-2 sm:mb-3">{t.transfers.tripType}</label>
                <div className="flex bg-gray-100 rounded-full p-1">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, tripType: 'one-way' })}
                    className={`flex-1 py-2.5 sm:py-3 px-3 sm:px-6 rounded-full text-xs sm:text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${
                      formData.tripType === 'one-way'
                        ? 'bg-[#00B4D8] text-white shadow-md'
                        : 'text-gray-600 hover:text-[#0A2463]'
                    }`}
                  >
                    <i className="ri-arrow-right-line mr-1 sm:mr-2"></i>
                    {t.transfers.oneWay}
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, tripType: 'round-trip' })}
                    className={`flex-1 py-2.5 sm:py-3 px-3 sm:px-6 rounded-full text-xs sm:text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${
                      formData.tripType === 'round-trip'
                        ? 'bg-[#00B4D8] text-white shadow-md'
                        : 'text-gray-600 hover:text-[#0A2463]'
                    }`}
                  >
                    <i className="ri-arrow-left-right-line mr-1 sm:mr-2"></i>
                    {t.transfers.roundTrip}
                  </button>
                </div>
              </div>

              {/* Destination */}
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-[#0A2463] mb-2">{t.transfers.destination}</label>
                <input
                  type="text"
                  name="destination"
                  value={formData.destination}
                  onChange={(e) => setFormData({...formData, destination: e.target.value})}
                  placeholder={t.transfers.destinationPlaceholder}
                  className="w-full px-3 sm:px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00B4D8] text-xs sm:text-sm"
                  required
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-[#0A2463] mb-2">{t.transfers.date}</label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                    className="w-full px-3 sm:px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00B4D8] text-xs sm:text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-[#0A2463] mb-2">{t.transfers.time}</label>
                  <input
                    type="time"
                    name="time"
                    value={formData.time}
                    onChange={(e) => setFormData({...formData, time: e.target.value})}
                    className="w-full px-3 sm:px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00B4D8] text-xs sm:text-sm"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-[#0A2463] mb-2">{t.transfers.passengersLabel}</label>
                  <select
                    name="passengers"
                    value={formData.passengers}
                    onChange={(e) => setFormData({...formData, passengers: e.target.value})}
                    className="w-full px-3 sm:px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00B4D8] text-xs sm:text-sm cursor-pointer"
                    required
                  >
                    {[1,2,3,4,5,6,7,8,9,10,11].map(num => {
                      const minP = getMinPassengers();
                      if (num < minP) return null;
                      return (
                        <option key={num} value={num}>{num} {num === 1 ? t.transfers.passenger : t.transfers.passengers}</option>
                      );
                    })}
                  </select>
                  {hasMinimum && (
                    <p className="text-[10px] sm:text-xs text-[#FF6B6B] mt-1.5 flex items-center gap-1">
                      <i className="ri-information-line"></i>
                      {t.transfers.minimumPeople}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-[#0A2463] mb-2">{t.transfers.serviceType}</label>
                  <select
                    name="vehicle"
                    value={formData.vehicle}
                    onChange={(e) => setFormData({...formData, vehicle: e.target.value})}
                    className="w-full px-3 sm:px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00B4D8] text-xs sm:text-sm cursor-pointer"
                    required
                  >
                    {vehicles.map(vehicle => (
                      <option key={vehicle.id} value={vehicle.id}>
                        {vehicle.name} ({t.transfers.capacity}: {vehicle.capacity})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Price Display */}
              <div className="bg-gradient-to-r from-[#00B4D8]/10 to-[#0A2463]/5 rounded-xl sm:rounded-2xl p-4 sm:p-6 space-y-2 sm:space-y-3">
                {formData.airport && (
                  <div className="flex justify-between items-center text-xs sm:text-sm">
                    <span className="text-gray-600">{t.transfers.pricePerPerson}</span>
                    <span className="text-[#0A2463] font-semibold">${pricePerPerson}</span>
                  </div>
                )}
                {formData.airport && (
                  <div className="flex justify-between items-center text-xs sm:text-sm">
                    <span className="text-gray-600">{t.transfers.passengersLabel}</span>
                    <span className="text-[#0A2463] font-semibold">{formData.passengers}</span>
                  </div>
                )}
                {formData.airport && (
                  <div className="flex justify-between items-center text-xs sm:text-sm">
                    <span className="text-gray-600">{t.transfers.tripType}</span>
                    <span className="text-[#0A2463] font-semibold">
                      {formData.tripType === 'round-trip' ? t.transfers.roundTrip : t.transfers.oneWay}
                    </span>
                  </div>
                )}
                <div className="border-t border-gray-200 pt-3 flex justify-between items-center">
                  <span className="text-[#0A2463] font-bold text-sm sm:text-base">{t.transfers.totalPrice}</span>
                  <span className="text-[#00B4D8] font-black text-2xl sm:text-3xl">${price}</span>
                </div>
              </div>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-3 sm:p-4">
                  <p className="text-green-800 text-xs sm:text-sm font-semibold">{t.transfers.successMessage}</p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 sm:p-4">
                  <p className="text-red-800 text-xs sm:text-sm font-semibold">{t.transfers.errorMessage}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#FF6B6B] text-white py-3.5 sm:py-4 rounded-full font-bold text-sm sm:text-base hover:bg-[#ff5252] transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting 
                  ? t.transfers.processing 
                  : (language === 'es' 
                      ? 'Procede al pago haciendo clic en el logo de PayPal' 
                      : 'Proceed with payment by clicking PayPal\'s logo')}
              </button>

              <div className="flex items-center justify-center gap-3 sm:gap-4 pt-3 sm:pt-4">
                <img src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 60 30'%3E%3Ctext x='5' y='20' font-family='Arial' font-size='14' font-weight='bold' fill='%23635BFF'%3ETripAdvisor%3C/text%3E%3C/svg%3E" alt="Stripe" className="h-5 sm:h-6" />
                <a href="https://www.paypal.com/ncp/payment/JUWXYKXZ84ACG" target="_blank" rel="noopener noreferrer" className="cursor-pointer hover:opacity-80 transition-opacity">
                  <img src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 60 30'%3E%3Ctext x='5' y='20' font-family='Arial' font-size='14' font-weight='bold' fill='%23003087'%3EPayPal%3C/text%3E%3C/svg%3E" alt="PayPal" className="h-5 sm:h-6" />
                </a>
                <div className="flex items-center gap-1">
                  <i className="ri-lock-line text-gray-400 text-sm sm:text-base"></i>
                  <span className="text-gray-500 text-[10px] sm:text-xs">{t.transfers.securePayment}</span>
                </div>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A2463] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M30 0c16.569 0 30 13.431 30 30 0 16.569-13.431 30-30 30C13.431 60 0 46.569 0 30 0 13.431 13.431 0 30 0z\' fill=\'%23ffffff\' fill-opacity=\'1\' fill-rule=\'evenodd\'/%3E%3C/svg%3E")'}}></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-12 lg:py-16 relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 sm:gap-12 mb-10 sm:mb-16">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                <img src="https://static.readdy.ai/image/b7cf4d15025d84086817049d392fc8bc/8ae7b2cd5c5fe056440d4fe4564ccd66.jpeg" alt="Leury Tours" className="h-8 sm:h-10 w-auto" />
                <span className="text-lg sm:text-xl font-bold">Leury Tours</span>
              </div>
              <p className="text-white/70 text-xs sm:text-sm mb-4 sm:mb-6">{t.transfers.heroDescription}</p>
              <div className="space-y-2 sm:space-y-3">
                <div className="flex items-center gap-2 sm:gap-3">
                  <i className="ri-phone-line text-[#48CAE4] text-sm"></i>
                  <span className="text-xs sm:text-sm">+1 (809)-461-9001</span>
                </div>
                <div className="flex items-center gap-2 sm:gap-3">
                  <i className="ri-mail-line text-[#48CAE4] text-sm"></i>
                  <span className="text-xs sm:text-sm break-all">leury2124@gmail.com</span>
                </div>
                <div className="flex items-center gap-2 sm:gap-3">
                  <i className="ri-map-pin-line text-[#48CAE4] text-sm"></i>
                  <span className="text-xs sm:text-sm">Puerto Plata, DR</span>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-xs sm:text-sm font-bold uppercase tracking-wider text-white/70 mb-4 sm:mb-6">{t.footer.stayUpdated}</h4>
              <p className="text-white/70 text-xs sm:text-sm mb-3 sm:mb-4">{t.footer.getBestPrices}</p>
              <div className="flex">
                <input type="email" placeholder={t.footer.emailPlaceholder} className="flex-1 min-w-0 bg-transparent border-b border-white/30 py-2 text-xs sm:text-sm text-white placeholder-white/50 focus:outline-none focus:border-[#48CAE4]" />
                <button className="ml-2 text-[#48CAE4] hover:text-white transition-colors cursor-pointer">
                  <i className="ri-arrow-right-line text-lg sm:text-xl"></i>
                </button>
              </div>
            </div>

            <div>
              <h4 className="text-xs sm:text-sm font-bold uppercase tracking-wider text-white/70 mb-4 sm:mb-6">{t.footer.explore}</h4>
              <ul className="space-y-2 sm:space-y-3">
                <li><Link to="/transfers" className="text-xs sm:text-sm hover:text-[#48CAE4] transition-colors">{t.footer.transfers}</Link></li>
                <li><Link to="/tours" className="text-xs sm:text-sm hover:text-[#48CAE4] transition-colors">{t.footer.tours}</Link></li>
                <li><Link to="/tours" className="text-xs sm:text-sm hover:text-[#48CAE4] transition-colors">{t.footer.cityTours}</Link></li>
                <li><a href="/#contact" className="text-xs sm:text-sm hover:text-[#48CAE4] transition-colors">{t.footer.customServices}</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-xs sm:text-sm font-bold uppercase tracking-wider text-white/70 mb-4 sm:mb-6">{t.footer.followUs}</h4>
              <ul className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                <li><a href="https://www.instagram.com/leury2124?igsh=bHdwNHcxbmVtOQ%3D%3D&utm_source=qr" target="_blank" rel="noopener noreferrer" className="text-xs sm:text-sm hover:text-[#48CAE4] transition-colors">Instagram</a></li>
                <li><a href="https://www.facebook.com/share/1VPJPAepMd/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer" className="text-xs sm:text-sm hover:text-[#48CAE4] transition-colors">Facebook</a></li>
                <li><a href="https://tripadvisor.com" target="_blank" rel="noopener noreferrer" className="text-xs sm:text-sm hover:text-[#48CAE4] transition-colors">TripAdvisor</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/10 pt-6 sm:pt-8">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-4">
              <p className="text-white/60 text-xs sm:text-sm text-center sm:text-left">{t.footer.allRightsReserved}</p>
              <div className="flex flex-wrap justify-center gap-4 sm:gap-6">
                <Link to="/privacy" className="text-white/60 text-xs sm:text-sm hover:text-white transition-colors">{t.footer.privacyPolicy}</Link>
                <Link to="/terms" className="text-white/60 text-xs sm:text-sm hover:text-white transition-colors">{t.footer.termsConditions}</Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
